import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Box, GlobalStyles, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/free-regular-svg-icons';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { RecaptchaEnterpriseProvider, useRecaptchaEnterpriseContext } from '@components/recaptcha';
import { getRecaptchaConfig } from '@/config';
import { useRequestPasswordReset } from '@services/user';

/** The reCAPTCHA action the API checks for a password reset request. */
export const PASSWORD_RESET_RECAPTCHA_ACTION = 'password_reset';

const RECAPTCHA_WAIT_MS = 5_000;
/** The whole check (reCAPTCHA and the API) gets this long; after that, the reset is left to Okta. */
const CHECK_DEADLINE_MS = 8_000;

/** Resolves to `fallback` if `promise` hasn't settled within `ms`; a later result is ignored. */
const withDeadline = <T,>(promise: Promise<T>, ms: number, fallback: T): Promise<T> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(fallback);
      },
    );
  });
const FORGOT_PASSWORD_LINK = '#okta-sign-in [data-se="forgot-password"]';
/**
 * The username from the widget's first screen, kept for the tab: after a reload, Okta resumes on its
 * password screen (its transaction is in sessionStorage too), so the first screen isn't shown again.
 */
const IDENTIFIER_STORAGE_KEY = 'lio.passwordReset.identifier';

const readStoredIdentifier = () => {
  try {
    return sessionStorage.getItem(IDENTIFIER_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

const storeIdentifier = (identifier: string | undefined) => {
  try {
    if (identifier) sessionStorage.setItem(IDENTIFIER_STORAGE_KEY, identifier);
    else sessionStorage.removeItem(IDENTIFIER_STORAGE_KEY);
  } catch {
    // Storage can be unavailable (private windows); the current screen's username still works.
  }
};

type ExecuteRecaptcha = (action: string) => Promise<string | null>;

/** The parts of the widget's afterRender context this uses. */
export type WidgetScreen = { formName?: string; authenticatorKey?: string };
export type AfterRenderCallback = (screen: WidgetScreen) => void;

interface OktaWidgetEvents {
  on: (event: 'afterRender', callback: AfterRenderCallback) => void;
  off: (event: 'afterRender', callback: AfterRenderCallback) => void;
}

/** Exposes the provider's reCAPTCHA execute, so a click outside React can get a token. */
const RecaptchaExecuteBridge = ({ onReady }: { onReady: (execute: ExecuteRecaptcha) => void }) => {
  const { executeRecaptcha } = useRecaptchaEnterpriseContext();
  useEffect(() => {
    onReady(executeRecaptcha);
  }, [executeRecaptcha, onReady]);
  return null;
};

/** Waits briefly for reCAPTCHA to load, in case "Forgot password" was clicked straight away. */
const getRecaptchaToken = async (executeRef: MutableRefObject<ExecuteRecaptcha | null>): Promise<string | null> => {
  const deadline = Date.now() + RECAPTCHA_WAIT_MS;
  while (!(executeRef.current && window.grecaptcha?.enterprise) && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  return executeRef.current ? executeRef.current(PASSWORD_RESET_RECAPTCHA_ACTION).catch(() => null) : null;
};

/**
 * Okta walks an address with no account through the same reset screens as a real one ("We sent you a
 * verification email") but sends nothing, so people wait for an email that never comes. When
 * "Forgot password" is clicked, this asks the API first. With no account, it shows its own card in
 * place of the widget, and the API emails them how to register. With an account, or if the check
 * fails or times out, Okta's reset carries on as before.
 *
 * The username comes from the widget's first screen. reCAPTCHA loads once the password screen shows.
 * Rendered beside the widget rather than inside Login's state: a Login re-render redraws the widget,
 * which would send the person back to the first screen.
 */
export const PasswordResetAccountCheck = ({ widget }: { widget: OktaWidgetEvents }) => {
  const { siteKey } = getRecaptchaConfig();
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const [noAccountEmail, setNoAccountEmail] = useState<string>();
  const formNameRef = useRef<string | undefined>(undefined);
  const onPasswordScreenRef = useRef(false);
  const identifierRef = useRef<string | undefined>(readStoredIdentifier());
  /** Lets the next "Forgot password" click through to Okta, once the address has an account. */
  const allowForgotRef = useRef(false);
  const checkingRef = useRef(false);
  /** Goes up each time the widget's first screen shows; a check only counts in the attempt it started in. */
  const attemptRef = useRef(0);
  const executeRecaptchaRef = useRef<ExecuteRecaptcha | null>(null);
  const { requestPasswordReset } = useRequestPasswordReset();

  const handleRecaptchaReady = useCallback((execute: ExecuteRecaptcha) => {
    executeRecaptchaRef.current = execute;
  }, []);

  /** True unless the API says there's no account; any failure, or no answer in time, leaves the reset to Okta. */
  const hasAccount = useCallback(
    (email: string) =>
      withDeadline(
        (async () => {
          const recaptchaToken = siteKey ? await getRecaptchaToken(executeRecaptchaRef) : null;
          const response = await requestPasswordReset({
            email,
            ...(recaptchaToken ? { recaptchaToken } : {}),
          });
          return response.hasAccount !== false;
        })(),
        CHECK_DEADLINE_MS,
        true,
      ),
    [siteKey, requestPasswordReset],
  );

  useEffect(() => {
    const onAfterRender = ({ formName, authenticatorKey }: WidgetScreen) => {
      formNameRef.current = formName;
      onPasswordScreenRef.current = formName === 'challenge-authenticator' && authenticatorKey === 'okta_password';
      if (formName === 'identify') {
        allowForgotRef.current = false;
        // A check still waiting belongs to the last attempt, so it mustn't hold up this one's click.
        checkingRef.current = false;
        attemptRef.current += 1;
      }
      if (onPasswordScreenRef.current) setLoadRecaptcha(true);
    };
    widget.on('afterRender', onAfterRender);
    return () => widget.off('afterRender', onAfterRender);
  }, [widget]);

  useEffect(() => {
    const onSubmit = (event: Event) => {
      if (formNameRef.current !== 'identify') return;
      if (!(event.target instanceof HTMLFormElement) || !event.target.closest('#okta-sign-in')) return;
      identifierRef.current = event.target.querySelector<HTMLInputElement>('input[name="identifier"]')?.value.trim();
      storeIdentifier(identifierRef.current);
    };
    const onClick = (event: Event) => {
      const link = event.target instanceof Element ? event.target.closest<HTMLElement>(FORGOT_PASSWORD_LINK) : null;
      if (!link) return;
      if (allowForgotRef.current) {
        allowForgotRef.current = false;
        return;
      }
      const email = identifierRef.current;
      if (!email?.includes('@')) return;
      // Hold Okta's reset until we know whether there's an account.
      event.preventDefault();
      event.stopPropagation();
      if (checkingRef.current) return;
      checkingRef.current = true;
      const attempt = attemptRef.current;
      link.setAttribute('aria-busy', 'true');
      void hasAccount(email).then((exists) => {
        // The person went back to sign in while we checked; a newer check owns the link now.
        if (attemptRef.current !== attempt) return;
        checkingRef.current = false;
        link.removeAttribute('aria-busy');
        // The person moved on while we checked (e.g. the widget left its password screen): drop the answer.
        if (!link.isConnected || identifierRef.current !== email || !onPasswordScreenRef.current) return;
        if (exists) {
          allowForgotRef.current = true;
          link.click();
        } else {
          setNoAccountEmail(email);
        }
      });
    };
    // Capture phase, so these run before the widget's own handlers.
    document.addEventListener('submit', onSubmit, true);
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('submit', onSubmit, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [hasAccount]);

  const handleBackToSignIn = () => {
    // Okta's own "Back to sign in" returns the widget to its first screen.
    document.querySelector<HTMLElement>('#okta-sign-in [data-se="cancel"]')?.click();
    setNoAccountEmail(undefined);
  };

  return (
    <>
      {loadRecaptcha && siteKey ? (
        <RecaptchaEnterpriseProvider siteKey={siteKey}>
          <RecaptchaExecuteBridge onReady={handleRecaptchaReady} />
        </RecaptchaEnterpriseProvider>
      ) : null}
      {noAccountEmail ? (
        <>
          {/* The widget stays mounted on its password screen, hidden, so "Back to sign in" can use it. */}
          <GlobalStyles styles={{ '[data-okta-widget]': { display: 'none' } }} />
          <EmailSentCard email={noAccountEmail} onBackToSignIn={handleBackToSignIn} />
        </>
      ) : null}
    </>
  );
};

/**
 * Laid out like the Okta widget's card, so it reads as the next step of the same flow. The email the
 * API sent explains there's no account and links to sign-up.
 */
const EmailSentCard = ({ email, onBackToSignIn }: { email: string; onBackToSignIn: () => void }) => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  return (
    <Box role='region' aria-labelledby='email-sent-title' sx={styles.card}>
      <Box sx={styles.content}>
        <Box sx={styles.icon} aria-hidden>
          <FontAwesomeIcon icon={faEnvelope} />
          <Box sx={styles.iconBadge}>
            <FontAwesomeIcon icon={faCheck} />
          </Box>
        </Box>
        <Typography id='email-sent-title' ref={titleRef} tabIndex={-1} component='h2' sx={styles.title}>
          Check your email
        </Typography>
        <Typography sx={styles.text}>We sent an email to</Typography>
        <Box sx={styles.emailChip}>{email}</Box>
        <Typography sx={styles.text}>Follow the steps in it to continue.</Typography>
        <Typography sx={styles.hint}>
          It can take a few minutes to arrive. If you don&#8217;t see it, check your spam folder.
        </Typography>
      </Box>
      <Box sx={styles.footer}>
        <Box component='button' type='button' onClick={onBackToSignIn} sx={styles.backLink}>
          Back to sign in
        </Box>
      </Box>
    </Box>
  );
};

const enter = {
  '@keyframes emailSentEnter': {
    from: { opacity: 0, transform: 'translateY(8px)' },
    to: { opacity: 1, transform: 'none' },
  },
  '@keyframes emailSentBadge': {
    from: { opacity: 0, transform: 'scale(0.4)' },
    to: { opacity: 1, transform: 'scale(1)' },
  },
  '@media (prefers-reduced-motion: reduce)': { '&, & *': { animation: 'none !important' } },
};

const styles = {
  card: {
    ...enter,
    boxSizing: 'border-box',
    // The widget's width.
    width: 438,
    maxWidth: '100%',
    mx: 'auto',
    p: '32px 32px 20px',
    borderRadius: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 8px 40px 0px',
    color: '#ffffff',
    fontFamily: '"Proxima Nova", sans-serif',
    // At least the height of the widget's password screen, which this replaces.
    minHeight: 327,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    animation: 'emailSentEnter 240ms ease-out both',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  icon: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 64,
    height: 64,
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.18)',
    fontSize: 26,
    color: '#ffffff',
    mb: 2.5,
  },
  iconBadge: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: '50%',
    backgroundColor: 'rgb(252, 205, 72)',
    color: 'rgb(28, 45, 64)',
    fontSize: 11,
    boxShadow: '0 0 0 3px rgba(43, 62, 84, 1)',
    animation: 'emailSentBadge 260ms 160ms ease-out both',
  },
  title: {
    fontFamily: '"Sofia Pro", sans-serif',
    fontSize: 20,
    fontWeight: 600,
    lineHeight: 1.2,
    color: '#ffffff',
    mb: 1.5,
    outline: 'none',
  },
  text: {
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
    color: '#ffffff',
  },
  emailChip: {
    maxWidth: '100%',
    my: 1,
    px: 1.5,
    py: 0.75,
    borderRadius: '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.4,
    overflowWrap: 'anywhere',
  },
  hint: {
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.5,
    color: 'rgba(255, 255, 255, 0.7)',
    maxWidth: 300,
    mt: 2,
    mb: 3,
  },
  footer: {
    borderTop: '1px solid rgba(255, 255, 255, 0.15)',
    pt: 2,
  },
  backLink: {
    p: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: '#ffffff',
    fontFamily: 'inherit',
    fontSize: 12,
    '&:hover': { textDecoration: 'underline', textUnderlineOffset: '4px' },
    '&:focus-visible': { outline: '2px solid rgb(252, 205, 72)', outlineOffset: '3px', borderRadius: '2px' },
  },
};
